import { defineStore } from "pinia";
import axios, { AxiosResponse } from "axios";
import { OrganisationModel } from "@Shared/Models/OrganisationModel";
import { PubAgreementResponseModel } from "@Shared/Models/PubAgreementResponseModel";
import { ContactInfoModel } from "@Shared/Models/ContactInfoModel";
import { OrganisationsResponseModel } from "@Shared/Models/OrganisationsResponseModel";
import { ServicesAndAgreementsListModel } from "@Shared/Models/ServicesAndAgreementsListModel";

const baseUrl = import.meta.env.VITE_APP_URL;

type OrganisationsModel = {
  organisation: OrganisationModel; // When createing new, store it here
  contactInfos: ContactInfoModel[];
  organisations: OrganisationModel[];
  total: Number;
  pubAgreement: PubAgreementResponseModel | undefined;
  servicesAndAgreements: ServicesAndAgreementsListModel[],
  loaded: boolean;
};

const defaultOrganisation: OrganisationModel = {
  id: "",
  displayName: "",
  organisationType: "",
  parentOrganisationId: "",
  organisationNumber: "",
  organisationCode: "",
  municipalityCode: "",
  schoolUnitCode: "",
  schoolType: "",
  legalForm: "",
  principalType: "",
  email: "",
  phoneNumber: "",
  addressList: [],
  contactInfoList: [],
  startDate: "",
  endDate: "",
  created: "",
  modified: "",
  status: "",

  licensesQty: 0
};

export const useOrganisationStore = defineStore({
  id: "organisation",
  state: (): OrganisationsModel => ({
    organisation: defaultOrganisation,
    organisations: [],
    contactInfos: [],
    total: 0,
    pubAgreement: {} as PubAgreementResponseModel,
    servicesAndAgreements: [],
    loaded: false
  }),

  getters: {
    getContactInfos: (state) => (): ContactInfoModel[] => {
      return state.contactInfos;
    },
    getContactInfoByInfoType:
      (state) =>
        (infoType: string): ContactInfoModel | null => {
          const ci = state.contactInfos.find(
            (ci: ContactInfoModel) => ci.infoType === infoType
          );
          if (ci) {
            return ci;
          }
          return null;
        },
    getOrganisationById:
      (state) =>
        (id: string): OrganisationModel | null => {
          const organisation = state.organisations.find(
            (org: OrganisationModel) => org.id === id
          );
          if (organisation) {
            return organisation;
          }

          return null;
        },
    getPubAgreement: (state) => () => {
      if (state.pubAgreement) {
        return state.pubAgreement;
      }
      return {};
    }
  },
  actions: {
    setDefaultOrganisation() {
      this.$patch({ organisation: defaultOrganisation });
    },
    setPubAgreement(data) {
      this.$patch({ pubAgreement: data });
    },
    async GetContactInfosFromApi(orgId) {
      let path = `${baseUrl}/organisation/${orgId}/getcontactinfos?useDistinct=true`;
      let response: AxiosResponse;
      try {
        response = await axios.get(path);
      } catch (error: any) {
        console.log("error :", error);
        return Promise.reject(new Error(error));
      }
      if (response.status === 200) {
        this.$patch({ contactInfos: response.data });
      }
      return Promise.resolve();
    },
    async GetOrganisations() {
      let path = `${baseUrl}/organisation/GetOrganisations`;
      let response: AxiosResponse<OrganisationsResponseModel>;
      try {
        response = await axios.get(path);
      } catch (error: any) {
        console.log("error :", error);
        this.$patch({ loaded: true })
        return Promise.reject(new Error(error));
      }

      if (response.status === 200) {
        this.$patch({ organisations: response.data.organisations });
      }

      this.$patch({ loaded: true })

      return Promise.resolve();
    },
    async GetOrgServicesAndAgreements(orgId) {
      let path = `${baseUrl}/agreement/${orgId}/servicesAndAgreements`;
      let response: AxiosResponse<ServicesAndAgreementsListModel[]>;
      try {
        response = await axios.get(path);
      } catch (error: any) {
        console.log("error :", error);
        return Promise.reject(new Error(error));
      }
      if (response.status === 200) {
        this.$patch({ servicesAndAgreements: response.data });
      }
      return Promise.resolve();
    },
    async SearchByOrgNr(orgNr: string) {
      let path = `${baseUrl}/organisation/SearchByOrgNr/${orgNr}`;
      let response: AxiosResponse;
      try {
        response = await axios.get(path);
      } catch (error: any) {
        console.log("error :", error);
        return Promise.reject(new Error(error));
      }
      if (response.status === 200) {
        const data = response.data;
        this.$patch({ organisation: data });

        return data;
      }
    },
      async Register(data: OrganisationModel) {
          let path = `${baseUrl}/organisation/Register`;
          try {
              const response = await axios.post<OrganisationModel>(path, data);
              if (response.status === 200) {
                  return Promise.resolve(true);
              } else {
                  console.error("Unexpected status code:", response.status);
                  return Promise.reject(new Error("Unexpected status code"));
              }
          } catch (error) {
              console.error("Error during registration:", error);
              return Promise.reject(error);
          }
      },
    async GetPubAgreements(orgId: string, ensureLatest: boolean, isOld?: boolean) {
        let path = `${baseUrl}/agreement/${orgId}/pubagreements`;
        if (ensureLatest) {
            path += "?ensureLatest=true";
        }
        if (isOld) {
            path += (path.includes("?") ? "&" : "?") + "old=true";
        }

      let response: AxiosResponse<PubAgreementResponseModel>;
      try {
        response = await axios.get(path);
      } catch (error: any) {
        if (error.name === "AxiosError") {
          this.$patch({ pubAgreement: undefined });
          return Promise.reject(error.response.data);
        }
        return Promise.reject(new Error(error));
      }
      if (response.status === 200) {
          const data = response.data;
        // Patch correct organisation
        this.$patch({ pubAgreement: data });
      }
      return Promise.resolve();
    },

    async SaveContactInfos(orgId: string, data: ContactInfoModel[]) {
      let path = `${baseUrl}/organisation/${orgId}/savecontactinfos`;
      let response: AxiosResponse;
      try {
        response = await axios.post(path, data);
      } catch (error: any) {
        console.log("error :", error);
        return Promise.reject(new Error(error));
      }
      return Promise.resolve(true);
    },
    async SignAgreement(
      organisationId: string,
      agreementId: string,
      pubAgreementId: number
    ) {
      let path = `${baseUrl}/agreement/Sign?organisationId=${organisationId}&agreementId=${agreementId}&pubAgreementId=${pubAgreementId}`;
      let response: AxiosResponse;
      try {
        response = await axios.put(path);
      } catch (error: any) {
        console.log("error :", error);
        return Promise.reject(new Error(error));
      }
      if (response.status === 200) {
        const data = response.data;
        this.$patch({ pubAgreement: data });
        return Promise.resolve(true);
      }
      return Promise.reject(response);
    }
  }
});
